import React from 'react';
import _ from 'lodash';
import { Button } from '@janus.team/janus-particles';
import {
  Container,
  ListGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { useHistory, Switch, Route } from 'react-router-dom';

import { useQueryGetProjectsFromJanus, useQueryGetUnmanagedProjects } from 'hooks/queries';
import { useParsedQueryString, useUpdatedTo } from 'projects/project-selector/hooks';
import { useRackspaceAccountContext } from 'customer/context';
import { LoadingIndicator } from 'components/loading/index';
import { ProjectImporterDrawer, routeParamsToOpenImportProjectsDrawer } from 'projects/project-selector/project-importer-drawer';
import { CreateProjectButton } from 'projects/index';
import { isRacker } from 'config';
import { ProjectDetailsDrawer } from './project-details-drawer';
import { ProjectListItem } from './project-list-item';
import { projectsPageFilterContext } from './context';
import { ProjectsToolBar } from './projects-toolbar';
import { matchesFilters } from './project-filter';

const matchesFilter = ({ project, filter, filterText }) => {
  const projectId = _.toLower(project.projectId);
  const projectName = _.toLower(project?.name || '');
  const projectFilterDataSet = [projectId, projectName];

  const isProjectMatched = _.some(projectFilterDataSet, _.method('includes', filterText));
  if (!filter) return isProjectMatched;

  return isProjectMatched && matchesFilters(project, filter);
};

const ImportProjectsButton = () => {
  const history = useHistory();
  const toOpenImportDrawer = useUpdatedTo(routeParamsToOpenImportProjectsDrawer());
  const openImportDrawer = () => history.replace(toOpenImportDrawer);

  const unmanagedProjects = useQueryGetUnmanagedProjects();
  const message = 'There are no available unmanaged projects in your Google Organization. Please create a new project in GCP.';

  const disabledButtonTooltip = () => (
    <OverlayTrigger overlay={<Tooltip id="tooltip-add-projects">{ message }</Tooltip>}>
      <span className="d-inline-flex">
        <Button type="button" disabled>
          Add projects
        </Button>
      </span>
    </OverlayTrigger>
  );

  return (
    unmanagedProjects?.data?.allProjects?.length > 0
      ? (
        <Button type="button" className="particles-button--primary" onClick={openImportDrawer}>
          <i className="fa fa-plus" />
          {' '}
          Add projects
        </Button>
      )
      : disabledButtonTooltip()
  );
};

const Action = () => (
  <Switch>
    <Route path="/racker/customers/:rackspaceAccountNumber">
      <CreateProjectButton />
    </Route>
    <Route>
      <ImportProjectsButton />
    </Route>
  </Switch>
);

const ShowProjectImportDrawer = () => (isRacker() ? null : <ProjectImporterDrawer />);

const EmptyProjectsPage = ({ ActionButton }) => {
  const { billing } = useRackspaceAccountContext();

  return (
    <Container>
      <p className="lead">Get started by adding GCP Projects to your Rackspace account.</p>
      <p>
        We have experts who can implement industry-best cloud monitoring technologies,&nbsp;
        suggest cost optimization recommendations, solve your IT challenges,&nbsp;
        and offer other value-added services — all backed by GCP-certified engineers and architects.
      </p>
      <p>
        Adding projects enables you to customize your service experience with Rackspace
        {billing.gcp.isResell && ' and enables consolidated billing for GCP infrastructure'}
        .
      </p>
      {
        billing.gcp.isNoResell
        && (
        <p>
          You will still be billed for infrastructure directly from Google.&nbsp;
          Adding projects does not modify project-level billing configuration.
        </p>
        )
      }
      <br />
      <ActionButton />
    </Container>
  );
};

export const ProjectsPage = () => {
  const [filter, setFilter] = React.useState('');
  const queryString = useParsedQueryString();
  const queryStringFilter = queryString?.filter;
  const projectsQuery = useQueryGetProjectsFromJanus();
  const hasLinkedProjects = !projectsQuery?.isLoading && (projectsQuery?.isSuccess && !!projectsQuery?.data?.projects.length);
  useQueryGetUnmanagedProjects({ enabled: !isRacker() }); // cache customer unlinked projects while fetching Janus projects

  const projectList = _.compact(projectsQuery?.data?.projects);
  const filteredProjects = _.filter(projectList, (project) => matchesFilter({ project, filter: queryStringFilter, filterText: filter }));

  return (
    <div className="rax-mgcp-projects main-content">
      <div className="rax-mgcp-projects-main-content">
        <header className="py-8">
          <Container className="d-flex justify-content-between align-items-center">
            <h1>GCP Projects</h1>
          </Container>
        </header>
        <LoadingIndicator isLoading={projectsQuery?.isLoading} centered message="Fetching GCP Projects">
          {hasLinkedProjects ? (
            <projectsPageFilterContext.Provider value={{ filter, setFilter }}>
              <ProjectsToolBar
                ActionButton={Action}
                projects={projectsQuery?.data?.projects}
                queryStringFilter={queryStringFilter}
                className="px-0 mb-6"
              />
              <Container className="h-100 overflow-hidden">
                { filteredProjects.length > 0 ? (
                  <ListGroup as="ul" variant="flush" className="border" aria-label="List of GCP Projects">
                    {_.map(filteredProjects, (project) => (
                      <ProjectListItem key={project?.projectId} project={project} />
                    ))}
                  </ListGroup>
                )
                  : <h2>No matching projects</h2> }
              </Container>
            </projectsPageFilterContext.Provider>
          )
            : <EmptyProjectsPage ActionButton={Action} />}
        </LoadingIndicator>
      </div>
      <ShowProjectImportDrawer />
      <ProjectDetailsDrawer showSystemsAccount={isRacker()} showManagementButtons={isRacker()} />
    </div>
  );
};
